import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Input } from "./Input.tsx";

type Block = {
  i: number;
  text: string;
  lineCount: number;
};

type SrtPreviewProps = {
  srt: string;
  /** Cuántos segmentos máximos mostrar. Si el SRT tiene más, se truncan. */
  maxBlocks: number;
  /** Altura del viewport del scroll en px. */
  viewportHeight?: number;
};

// --- Constantes internas ---

// Por encima de esta cantidad de bloques visibles, virtualizamos. El usuario
// no ve este número — es un detalle de performance.
const VIRTUALIZE_AUTO_THRESHOLD = 500;

// Altura estimada por bloque para la virtualización. Imperfecta pero
// suficiente — el browser absorbe pequeñas diferencias.
const BASE_BLOCK_HEIGHT = 80;
const LINE_HEIGHT = 18;

function blockHeight(b: Block): number {
  const extraLines = Math.max(0, b.lineCount - 3);
  return BASE_BLOCK_HEIGHT + extraLines * LINE_HEIGHT;
}

function splitIntoBlocks(srt: string): Block[] {
  const normalized = srt.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const rawBlocks = normalized.split(/\n\s*\n/);
  const result: Block[] = [];
  let i = 0;
  for (const raw of rawBlocks) {
    const trimmed = raw.replace(/^\n+|\n+$/g, "");
    if (trimmed.length === 0) continue;
    const lines = trimmed.split("\n");
    if (lines.length < 3) continue;
    if (!Number.isInteger(Number(lines[0].trim()))) continue;
    result.push({ i: i++, text: trimmed, lineCount: lines.length });
  }
  return result;
}

export function VirtualSrtPreview({
  srt,
  maxBlocks,
  viewportHeight = 480,
}: SrtPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [search, setSearch] = useState("");
  const [matchCursor, setMatchCursor] = useState(0);

  // Parseo del SRT completo. Caro pero solo se hace cuando cambia el SRT.
  const allBlocks = useMemo(() => splitIntoBlocks(srt), [srt]);

  // Aplicar el límite del usuario. Esta es la lista que efectivamente se
  // renderiza, busca, virtualiza, todo.
  const visibleBlocks = useMemo(
    () => allBlocks.slice(0, Math.max(0, maxBlocks)),
    [allBlocks, maxBlocks],
  );

  // Decidir si activar virtualización (transparente al usuario).
  const shouldVirtualize = visibleBlocks.length > VIRTUALIZE_AUTO_THRESHOLD;

  // Offsets cumulativos para los spacers (solo aplica si virtualizamos).
  const { totalHeight, offsets } = useMemo(() => {
    const offs = new Array<number>(visibleBlocks.length);
    let acc = 0;
    for (let i = 0; i < visibleBlocks.length; i++) {
      offs[i] = acc;
      acc += blockHeight(visibleBlocks[i]);
    }
    return { totalHeight: acc, offsets: offs };
  }, [visibleBlocks]);

  // Reset del scroll cuando cambian los bloques visibles o el modo.
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
      setScrollTop(0);
    }
  }, [shouldVirtualize, maxBlocks]);

  // Búsqueda dentro de los bloques visibles únicamente.
  const matchingIndices = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length === 0) return [];
    const result: number[] = [];
    for (const b of visibleBlocks) {
      if (b.text.toLowerCase().includes(q)) result.push(b.i);
    }
    return result;
  }, [visibleBlocks, search]);

  useEffect(() => {
    setMatchCursor(0);
  }, [search, maxBlocks]);

  // Scroll preciso al match centrado.
  // En modo virtual: doble pase con measurement real del DOM.
  // En modo flat: scrollIntoView directo del elemento.
  const targetMatchIdx =
    matchingIndices.length > 0 ? matchingIndices[matchCursor] ?? null : null;

  useEffect(() => {
    if (targetMatchIdx === null) return;
    const container = containerRef.current;
    if (!container) return;

    const blockInList = visibleBlocks.find((b) => b.i === targetMatchIdx);
    if (!blockInList) return;

    if (shouldVirtualize) {
      // En modo virtual: primero saltamos al offset estimado para forzar
      // que React monte el bloque, luego medimos el DOM real.
      const positionInList = visibleBlocks.indexOf(blockInList);
      const offset = offsets[positionInList] ?? 0;
      const h = blockHeight(blockInList);
      const estimated = Math.max(0, offset - viewportHeight / 2 + h / 2);
      container.scrollTo({ top: estimated, behavior: "instant" });

      requestAnimationFrame(() => {
        const targetEl = container.querySelector<HTMLElement>(
          `[data-block-idx="${targetMatchIdx}"]`,
        );
        if (targetEl) {
          targetEl.scrollIntoView({ block: "center", behavior: "instant" });
        }
      });
    } else {
      // Modo flat: scrollIntoView directo, smooth.
      const targetEl = container.querySelector<HTMLElement>(
        `[data-block-idx="${targetMatchIdx}"]`,
      );
      if (targetEl) {
        targetEl.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }
  }, [targetMatchIdx, visibleBlocks, offsets, viewportHeight, shouldVirtualize]);

  const goToNextMatch = () => {
    if (matchingIndices.length === 0) return;
    setMatchCursor((c) => (c + 1) % matchingIndices.length);
  };
  const goToPrevMatch = () => {
    if (matchingIndices.length === 0) return;
    setMatchCursor((c) => (c - 1 + matchingIndices.length) % matchingIndices.length);
  };

  // Cálculo del rango visible para virtualización.
  const { startIdx, endIdx, topSpacer, bottomSpacer } = useMemo(() => {
    if (!shouldVirtualize || visibleBlocks.length === 0) {
      return {
        startIdx: 0,
        endIdx: visibleBlocks.length,
        topSpacer: 0,
        bottomSpacer: 0,
      };
    }

    const BUFFER = 8;
    let start = 0;
    for (let i = 0; i < visibleBlocks.length; i++) {
      if (offsets[i] + blockHeight(visibleBlocks[i]) >= scrollTop) {
        start = i;
        break;
      }
      start = i;
    }
    start = Math.max(0, start - BUFFER);

    let end = start;
    const visibleEnd = scrollTop + viewportHeight;
    for (let i = start; i < visibleBlocks.length; i++) {
      end = i + 1;
      if (offsets[i] >= visibleEnd) break;
    }
    end = Math.min(visibleBlocks.length, end + BUFFER);

    const top = offsets[start] ?? 0;
    const lastVisibleEnd =
      offsets[end - 1] !== undefined
        ? offsets[end - 1] + blockHeight(visibleBlocks[end - 1])
        : 0;
    const bottom = Math.max(0, totalHeight - lastVisibleEnd);

    return { startIdx: start, endIdx: end, topSpacer: top, bottomSpacer: bottom };
  }, [shouldVirtualize, scrollTop, viewportHeight, offsets, visibleBlocks, totalHeight]);

  // Highlighting de matches dentro del texto.
  const renderBlockText = (text: string): React.ReactNode => {
    const q = search.trim();
    if (q.length === 0) return text;

    const lower = text.toLowerCase();
    const lowerQ = q.toLowerCase();
    const result: React.ReactNode[] = [];
    let cursor = 0;
    let key = 0;
    while (cursor < text.length) {
      const idx = lower.indexOf(lowerQ, cursor);
      if (idx === -1) {
        result.push(text.slice(cursor));
        break;
      }
      if (idx > cursor) result.push(text.slice(cursor, idx));
      result.push(<Match key={key++}>{text.slice(idx, idx + q.length)}</Match>);
      cursor = idx + q.length;
    }
    return result;
  };

  const blocksToRender = visibleBlocks.slice(startIdx, endIdx);
  const matchPosition =
    matchingIndices.length > 0
      ? `${matchCursor + 1} / ${matchingIndices.length}`
      : "Sin resultados";

  const isTruncated = visibleBlocks.length < allBlocks.length;

  return (
    <Wrapper>
      <Toolbar>
        <SearchInputWrapper>
          <Input
            placeholder={
              visibleBlocks.length === 0
                ? "Sin contenido"
                : `Buscar en ${visibleBlocks.length.toLocaleString()} segmentos…`
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchInputWrapper>

        {search.trim().length > 0 && (
          <MatchControls>
            <MatchCount>{matchPosition}</MatchCount>
            <MatchButton
              type="button"
              onClick={goToPrevMatch}
              disabled={matchingIndices.length === 0}
              aria-label="Match anterior"
            >
              ↑
            </MatchButton>
            <MatchButton
              type="button"
              onClick={goToNextMatch}
              disabled={matchingIndices.length === 0}
              aria-label="Siguiente match"
            >
              ↓
            </MatchButton>
          </MatchControls>
        )}

        <CountBadge $truncated={isTruncated}>
          {isTruncated
            ? `${visibleBlocks.length.toLocaleString()} / ${allBlocks.length.toLocaleString()} segs`
            : `${visibleBlocks.length.toLocaleString()} segs`}
        </CountBadge>
      </Toolbar>

      <ScrollContainer
        ref={containerRef}
        style={{ height: viewportHeight }}
        onScroll={(e) => setScrollTop((e.currentTarget as HTMLDivElement).scrollTop)}
      >
        {shouldVirtualize ? (
          <VirtualizedInner style={{ height: totalHeight }}>
            <div style={{ height: topSpacer }} />
            {blocksToRender.map((b) => (
              <BlockPre key={b.i} data-block-idx={b.i}>
                {renderBlockText(b.text)}
              </BlockPre>
            ))}
            <div style={{ height: bottomSpacer }} />
          </VirtualizedInner>
        ) : (
          <FlatInner>
            {visibleBlocks.map((b) => (
              <BlockPre key={b.i} data-block-idx={b.i}>
                {renderBlockText(b.text)}
              </BlockPre>
            ))}
          </FlatInner>
        )}
      </ScrollContainer>

      {isTruncated && (
        <TruncationHint>
          Mostrando los primeros {visibleBlocks.length.toLocaleString()} de{" "}
          {allBlocks.length.toLocaleString()} segmentos. Sube el slider para ver más.
          El archivo guardado contendrá todos los segmentos sin importar este límite.
        </TruncationHint>
      )}
    </Wrapper>
  );
}

// --- Estilos ---

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.sm};
  background: ${({ theme }) => theme.color.surface};
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
`;

const SearchInputWrapper = styled.div`
  flex: 1;
`;

const MatchControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
`;

const MatchCount = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.mono};
  min-width: 5rem;
  text-align: right;
`;

const MatchButton = styled.button`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.borderStrong};
  background: ${({ theme }) => theme.color.surface};
  cursor: pointer;
  color: ${({ theme }) => theme.color.textPrimary};
  font-size: ${({ theme }) => theme.font.size.sm};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.color.background};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CountBadge = styled.span<{ $truncated: boolean }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  font-family: ${({ theme }) => theme.font.mono};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme, $truncated }) =>
    $truncated ? theme.color.warningMuted : theme.color.background};
  color: ${({ theme, $truncated }) =>
    $truncated ? theme.color.warning : theme.color.textMuted};
  white-space: nowrap;
`;

const ScrollContainer = styled.div`
  background: #1a1a1a;
  overflow-y: auto;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.font.size.xs};
  color: #dfd;
`;

const VirtualizedInner = styled.div`
  position: relative;
`;

const FlatInner = styled.div`
  padding: ${({ theme }) => theme.space.sm} 0;
`;

const BlockPre = styled.pre`
  margin: 0;
  padding: ${({ theme }) => `${theme.space.xs} ${theme.space.md}`};
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
`;

const Match = styled.mark`
  background: ${({ theme }) => theme.color.warning};
  color: white;
  border-radius: 2px;
  padding: 0 2px;
`;

const TruncationHint = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.warning};
  background: ${({ theme }) => theme.color.warningMuted};
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  border-top: 1px solid ${({ theme }) => theme.color.border};
`;